import { Component, HostBinding } from '@angular/core';
import dayjs from 'dayjs';
import {
    IDwRewardShipAdvancedFilterParameter,
} from '../../../models/dw-rewardship-advanced-filter-parameter.interface';
import { RewardshipService } from '../../../services/rewardship.service';
import { ISearchParameterChipData } from './models/search-parameter-chip-data.interface';

@Component({
    selector: 'upsc-dw-rewardship-advanced-filter-chips',
    templateUrl: './dw-rewardship-advanced-filter-chips.component.html',
    styleUrls: ['./dw-rewardship-advanced-filter-chips.component.scss'],
})
export class DwRewardshipAdvancedFilterChipsComponent {
    @HostBinding('class') public hostClass = 'dw-rewardship-advanced-filter-chips';

    public chipDataList: ISearchParameterChipData[];

    private keyIcon = {
        dateRange: 'calendar_month',
        invoiceBalance: 'attach_money',
        paymentStatuses: 'description',
        pointStatuses: 'timeline',
    };

    constructor(private rewardShipService: RewardshipService) {
        this.rewardShipService.advancedFilterParameterChanged$.subscribe(
            (parameter) => {
                this.chipDataList = this.convertParameterToChipDataList(parameter);
            },
        );
    }

    public onParameterRemoved(chipData: ISearchParameterChipData) {
        this.chipDataList = this.chipDataList.filter(item => item.label !== chipData.label);
        const parameter = this.convertChipDataListToParameter(this.chipDataList);
        this.rewardShipService.advancedFilterParameterChanged$.next(parameter);
    }

    private convertParameterToChipDataList(params: IDwRewardShipAdvancedFilterParameter): ISearchParameterChipData[] {
        const mappings = [
            {
                check: () => params.dateRangeType && params.dateRangeStart && params.dateRangeEnd,
                label: params.dateRangeType === 'Invoice Date' ? 'Invoice Date Range' : 'Due Date Range',
                value: () => `${ dayjs(params.dateRangeStart).format('MM/DD/YYYY') } - ${ dayjs(params.dateRangeEnd).format('MM/DD/YYYY') }`,
                icon: this.keyIcon.dateRange,
            },
            {
                check: () => params.invoiceBalanceMin !== undefined && params.invoiceBalanceMax !== undefined,
                label: 'Invoice Balance',
                value: () => `$${ params.invoiceBalanceMin.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }) } - $${ params.invoiceBalanceMax.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }) }`,
                icon: this.keyIcon.invoiceBalance,
            },
            {
                check: () => params.paymentStatuses && params.paymentStatuses.length > 0,
                label: 'Claim Status',
                value: () => params.paymentStatuses.join(','),
                icon: this.keyIcon.paymentStatuses,
            },
            {
                check: () => params.pointStatuses && params.pointStatuses.length > 0,
                label: 'Points Status',
                value: () => params.pointStatuses.join(','),
                icon: this.keyIcon.pointStatuses,
            },
        ];

        return mappings.filter(mapping => mapping.check()).map(mapping => ({
            label: mapping.label,
            value: mapping.value(),
            icon: mapping.icon,
        }));
    }

    private convertChipDataListToParameter(data: ISearchParameterChipData[]): IDwRewardShipAdvancedFilterParameter {
        const originalFormat: IDwRewardShipAdvancedFilterParameter = {};

        data.forEach(item => {
            switch (item.label) {
                case 'Invoice Date Range':
                case 'Due Date Range': {
                    const [startStr, endStr] = item.value.split(' - ');
                    const dateRangeStart = dayjs(startStr, 'MM/DD/YYYY').startOf('day').toDate();
                    const dateRangeEnd = dayjs(endStr, 'MM/DD/YYYY').endOf('day').toDate();

                    if (dateRangeStart && dateRangeEnd) {
                        originalFormat.dateRangeType = item.label === 'Invoice Date Range' ? 'Invoice Date' : 'Due Date';
                        originalFormat.dateRangeStart = dateRangeStart;
                        originalFormat.dateRangeEnd = dateRangeEnd;
                    }

                    break;
                }
                case 'Invoice Balance': {
                    const balances = item.value.split(' - ')
                                         .map((balanceStr: string) => parseFloat(balanceStr.replace(/[$,]/g, '')));
                    if (balances.length === 2) {
                        originalFormat.invoiceBalanceMin = balances[0];
                        originalFormat.invoiceBalanceMax = balances[1];
                    }

                    break;
                }
                case 'Claim Status':
                    if (item.value) {
                        originalFormat.paymentStatuses = item.value.split(',');
                    }

                    break;
                case 'Points Status':
                    if (item.value) {
                        originalFormat.pointStatuses = item.value.split(',');
                    }

                    break;
            }
        });

        return originalFormat;
    }
}
